import { useState, useRef } from 'react';
import { Send, X, Image as ImageIcon, FileText, Video, Paperclip, LayoutTemplate } from 'lucide-react';
import { useWhatsappWebMutations, whatsappError } from '../whatsappWebApi';
import { toast } from 'react-hot-toast';
import { Spinner } from '@/shared/components/ui/Spinner';
import { cn } from '@/shared/utils/utils';
import { TemplatePicker } from './TemplatePicker';

interface MessageComposerProps {
  chatId: string;
  /** Business API inbox: templates are available. */
  businessApi: boolean;
  /** WhatsApp's 24-hour window — when closed, only a template can be sent. */
  windowOpen: boolean;
  contactName?: string;
}

export function MessageComposer({ chatId, businessApi, windowOpen, contactName }: MessageComposerProps) {
  const [body, setBody] = useState('');
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [attachment, setAttachment] = useState<{ file: File; type: string; base64: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);

  const { send, sendMedia } = useWhatsappWebMutations();
  const canType = !businessApi || windowOpen;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Files up to 15 MB can be sent.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      if (!result) return;
      setAttachment({ file, type: file.type || 'application/octet-stream', base64: result.split(',')[1] });
      setAttachMenuOpen(false);
    };
    reader.readAsDataURL(file);
  };

  const pick = (accept: string) => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = accept;
      fileInputRef.current.click();
    }
  };

  const handleSend = () => {
    if (!body.trim() && !attachment) return;
    const to = chatId.replace('@c.us', '').replace('@g.us', '');
    if (attachment) {
      sendMedia.mutate(
        { to, body: body.trim(), data: attachment.base64, mimetype: attachment.type, filename: attachment.file.name },
        {
          onSuccess: () => { setBody(''); setAttachment(null); },
          onError: (err) => toast.error(whatsappError(err, 'Could not send the file.')),
        },
      );
    } else {
      send.mutate(
        { to, body: body.trim() },
        {
          onSuccess: () => {
            setBody('');
            if (textRef.current) textRef.current.style.height = 'auto';
          },
          onError: (err) => toast.error(whatsappError(err, 'Could not send the message.')),
        },
      );
    }
  };

  const isPending = send.isPending || sendMedia.isPending;

  return (
    <div className="relative flex shrink-0 flex-col justify-center bg-[#f0f2f5] px-3 py-2">
      {attachment && (
        <div className="absolute bottom-full left-0 right-0 z-10 flex min-h-[150px] flex-col items-center justify-center border-t border-[#d1d7db] bg-[#e9edef] p-4 shadow-lg">
          <button type="button" className="absolute right-4 top-4 rounded-full bg-white p-2 text-gray-500 shadow hover:bg-gray-50" onClick={() => setAttachment(null)} aria-label="Remove file">
            <X className="h-5 w-5" />
          </button>
          <div className="flex w-full max-w-sm flex-col items-center gap-3 rounded-lg bg-white p-4 shadow-sm">
            {attachment.type.startsWith('image/') ? (
              <img src={`data:${attachment.type};base64,${attachment.base64}`} alt="preview" className="max-h-48 rounded object-contain" />
            ) : (
              <div className="flex h-24 w-24 items-center justify-center rounded bg-gray-100 text-gray-400">
                {attachment.type.startsWith('video/') ? <Video className="h-10 w-10" /> : <FileText className="h-10 w-10" />}
              </div>
            )}
            <span className="w-full truncate text-center text-body-sm text-gray-600">{attachment.file.name}</span>
            <p className="text-xs text-gray-400">Add a caption below, then send</p>
          </div>
        </div>
      )}

      {!canType ? (
        <div className="flex flex-col items-center gap-2 py-1 text-center sm:flex-row sm:justify-between sm:text-left">
          <p className="text-[13px] text-[#54656f]">
            No message from this customer in the last 24 hours — WhatsApp allows only an approved template.
          </p>
          <button
            type="button"
            onClick={() => setTemplatesOpen(true)}
            className="inline-flex shrink-0 items-center gap-2 rounded-full bg-[#00a884] px-4 py-2 text-[14px] font-medium text-white hover:bg-[#017561]"
          >
            <LayoutTemplate className="h-4 w-4" /> Send a template
          </button>
        </div>
      ) : (
        <div className="flex w-full items-end gap-1">
          {businessApi && (
            <button type="button" className="shrink-0 rounded-full p-2 text-[#54656f] hover:bg-black/5 hover:text-[#111b21]" onClick={() => setTemplatesOpen(true)} title="Send a template">
              <LayoutTemplate className="h-[22px] w-[22px]" />
            </button>
          )}
          <div className="relative shrink-0">
            <button
              type="button"
              className={cn('rounded-full p-2 text-[#54656f] hover:bg-black/5 hover:text-[#111b21]', attachMenuOpen && 'bg-black/10')}
              onClick={() => setAttachMenuOpen(!attachMenuOpen)}
              title="Attach a file"
            >
              <Paperclip className="h-[22px] w-[22px]" />
            </button>
            {attachMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setAttachMenuOpen(false)} />
                <div className="absolute bottom-[52px] left-0 z-20 flex w-[220px] flex-col gap-1 rounded-2xl bg-white p-2 shadow-[0_2px_5px_0_rgba(11,20,26,0.26),0_2px_10px_0_rgba(11,20,26,0.16)]">
                  <button type="button" className="flex items-center gap-3 rounded-lg px-2 py-2 text-[#111b21] hover:bg-[#f5f6f6]" onClick={() => pick('application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')}>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#7f66ff] text-white"><FileText className="h-5 w-5" /></span>
                    <span className="text-[15px]">Document</span>
                  </button>
                  <button type="button" className="flex items-center gap-3 rounded-lg px-2 py-2 text-[#111b21] hover:bg-[#f5f6f6]" onClick={() => pick('image/jpeg,image/png,image/webp,video/mp4')}>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#007bfc] text-white"><ImageIcon className="h-5 w-5" /></span>
                    <span className="text-[15px]">Photo or video</span>
                  </button>
                </div>
              </>
            )}
          </div>

          <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />

          <div className="flex min-h-[42px] flex-1 items-center rounded-[8px] border border-white bg-white px-3 py-[9px] shadow-sm focus-within:border-[#00a884]">
            <textarea
              ref={textRef}
              rows={1}
              placeholder={attachment ? 'Add a caption' : 'Type a message'}
              className="max-h-32 w-full resize-none border-none bg-transparent p-0 text-[15px] leading-tight text-[#111b21] outline-none focus:ring-0"
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
          </div>

          <button
            type="button"
            className="ml-1 flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-[#00a884] text-white hover:bg-[#017561] disabled:bg-[#b5c6c0]"
            onClick={handleSend}
            disabled={(!body.trim() && !attachment) || isPending}
            title="Send"
          >
            {isPending ? <Spinner className="h-5 w-5 text-white" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
      )}

      {businessApi && (
        <TemplatePicker open={templatesOpen} onClose={() => setTemplatesOpen(false)} chatId={chatId} contactName={contactName} />
      )}
    </div>
  );
}
